import { useState } from "react";
import { MessageCircleIcon, PhoneCallIcon } from "lucide-react";

import { ContactUsForm } from "@/components";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";

import { useSuccessToast } from "@/hooks";
import { env } from "@/lib/env";

import corbinBlackAndWhite from "@/assets/corbin_black_and_white.webp";
import simbaBlackAndWhite from "@/assets/simba_black_and_white.webp";

enum Tab {
  Menu,
  Call,
  Message,
}

const MenuTab = ({ setTab }: { setTab: (tab: Tab) => void }) => {
  return (
    <div className="flex flex-col gap-8">
      <p className="text-2xl font-semibold text-slate-700">
        Contact Laborup Support
      </p>
      <div className="flex flex-col gap-4">
        <Button
          onClick={() => {
            setTab(Tab.Call);
          }}
          variant="outlineSecondary"
        >
          <PhoneCallIcon /> Call us
        </Button>
        <Button
          onClick={() => {
            setTab(Tab.Message);
          }}
          variant="outlineSecondary"
        >
          <MessageCircleIcon />
          Send us a message
        </Button>
      </div>
    </div>
  );
};

const CallTab = ({ setTab }: { setTab: (tab: Tab) => void }) => {
  return (
    <div className="flex flex-col items-center gap-10">
      <div className="flex w-[300px] flex-col items-center gap-1">
        <p className="text-2xl font-semibold text-slate-700">
          Call us anytime!
        </p>
        <p className="text-center text-sm text-slate-700">
          Feel free to call or text us about any questions or issues you&apos;re
          having
        </p>
      </div>
      <div className="w-full">
        <div className="flex items-center justify-center gap-3 border-y border-slate-200 p-3">
          <img
            className="h-[50px] w-[50px] rounded-full"
            src={simbaBlackAndWhite}
          />
          <div className="flex flex-col items-center">
            <p className="font-bold text-slate-700">Simba Jonga</p>
            <p className="font-medium text-slate-700">
              {env.getSimbaPhoneNumber()}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 border-b border-slate-200 p-3">
          <img
            className="h-[50px] w-[50px] rounded-full"
            src={corbinBlackAndWhite}
          />
          <div className="flex flex-col items-center">
            <p className="font-bold text-slate-700">Corbin Hooker</p>
            <p className="font-medium text-slate-700">
              {env.getCorbinPhoneNumber()}
            </p>
          </div>
        </div>
      </div>
      <div className="flex w-full justify-end">
        <Button
          className="rounded-full"
          onClick={() => {
            setTab(Tab.Menu);
          }}
          variant="secondary"
        >
          Back
        </Button>
      </div>
    </div>
  );
};

const MessageTab = ({
  onOpenChange,
  setTab,
}: {
  onOpenChange: (open: boolean) => void;
  setTab: (tab: Tab) => void;
}) => {
  const successToast = useSuccessToast();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <p className="text-2xl font-semibold text-slate-700">
          Send us a message
        </p>
        <p className="text-sm text-slate-700">
          We&apos;ll get back to you as quickly as possible
        </p>
      </div>
      <ContactUsForm
        handleBack={() => {
          setTab(Tab.Menu);
        }}
        handleSuccess={() => {
          onOpenChange(false);

          setTimeout(() => {
            setTab(Tab.Menu);
          }, 100);

          // Show a success toast after the message is sent with a delay
          // to ensure the dialog is closed and to reduce flickering.
          setTimeout(() => {
            successToast({ description: "Message sent successfully" });
          }, 500);
        }}
      />
    </div>
  );
};

export const ContactUsDialog = ({
  onOpenChange,
  open,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const [tab, setTab] = useState(Tab.Menu);

  const renderTab = (): React.ReactElement => {
    switch (tab) {
      case Tab.Menu:
        return <MenuTab setTab={setTab} />;
      case Tab.Call:
        return <CallTab setTab={setTab} />;
      case Tab.Message:
        return <MessageTab onOpenChange={onOpenChange} setTab={setTab} />;
    }
  };

  return (
    <Dialog
      onOpenChange={(isOpen) => {
        // If the dialog is being closed, ensure that the tab is set to the menu tab.
        if (!isOpen) {
          setTimeout(() => {
            setTab(Tab.Menu);
          }, 100);
        }

        onOpenChange(isOpen);
      }}
      open={open}
    >
      <DialogContent>
        {/* This ensures spacing between top of dialog and content. */}
        <DialogHeader />
        {renderTab()}
      </DialogContent>
    </Dialog>
  );
};
